// ** React Imports
import { useEffect } from 'react';

// ** Next Import
import { useRouter } from 'next/router';

// ** Icon Imports
import FaIconUvilo, { type IconPrefix } from '@erikdakoda/ui/components/FaIconUvilo';

// ** Custom Components Imports
import VerticalNavItems from './VerticalNavItems';

// ** Configs Import
import themeConfig from '../../../../config';
// ** Type Import
import type { LayoutProps, NavGroup } from '../../../../types';
// ** Utils
import { hasActiveChild, removeChildren } from '../../../../layouts/utils';

// ** MUI Imports
import Box, { type BoxProps } from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Collapse from '@mui/material/Collapse';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';
// ** Third Party Imports
import clsx from 'clsx';
import CanViewNavLink from '../../CanViewNavLink';
import {
  collapsedNavItemInsetPx,
  hiddenNavLabelSx,
  NAV_ITEM_ICON_LABEL_GAP,
  NAV_ITEM_ICON_WIDTH_PX,
} from './navItemLayout';

interface Props {
  item: NavGroup;
  navHover: boolean;
  parent?: NavGroup;
  navVisible?: boolean;
  groupActive: string[];
  collapsedNavWidth: number;
  currentActiveGroup: string[];
  navigationBorderWidth: number;
  settings: LayoutProps['settings'];
  isSubToSub?: NavGroup | undefined;
  saveSettings: LayoutProps['saveSettings'];
  setGroupActive: (values: string[]) => void;
  setCurrentActiveGroup: (items: string[]) => void;
}

const MenuItemTextWrapper = styled(Box)<BoxProps>(({ theme }) => ({
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
  justifyContent: 'space-between',
  transition: 'opacity .25s ease-in-out',
  ...(themeConfig.config.menuTextTruncate && { overflow: 'hidden' }),
}));

const VerticalNavGroup = (props: Props) => {
  // ** Props
  const {
    item,
    parent,
    settings,
    navHover,
    isSubToSub,
    groupActive,
    setGroupActive,
    currentActiveGroup,
    setCurrentActiveGroup,
    collapsedNavWidth,
  } = props;

  // ** Hooks & Vars
  const router = useRouter();
  const currentURL = router.asPath;
  const { navCollapsed, verticalNavToggleType } = settings;
  const hideNavLabels = navCollapsed && !navHover;
  const collapsedIconInsetPx = collapsedNavItemInsetPx(collapsedNavWidth, NAV_ITEM_ICON_WIDTH_PX);

  // ** Accordion menu group open toggle
  const toggleActiveGroup = (item: NavGroup, parent: NavGroup | undefined) => {
    let openGroup = groupActive;

    // ** If Group is already open and clicked, close the group
    if (openGroup.includes(item.title)) {
      openGroup.splice(openGroup.indexOf(item.title), 1);

      // If clicked Group has open group children, Also remove those children to close those groups
      if (item.children) {
        removeChildren(item.children, openGroup, currentActiveGroup);
      }
    } else if (parent) {
      // ** If Group clicked is the child of an open group, first remove all the open groups under that parent
      if (parent.children) {
        removeChildren(parent.children, openGroup, currentActiveGroup);
      }

      // ** After removing all the open groups under that parent, add the clicked group to open group array
      if (!openGroup.includes(item.title)) {
        openGroup.push(item.title);
      }
    } else {
      // ** If clicked on another group that is not active or open, create openGroup array from scratch

      // ** Empty Open Group array
      openGroup = [];

      // ** push Current Active Group To Open Group array
      if (currentActiveGroup.every((elem) => groupActive.includes(elem))) {
        openGroup.push(...currentActiveGroup);
      }

      // ** Push current clicked group item to Open Group array
      if (!openGroup.includes(item.title)) {
        openGroup.push(item.title);
      }
    }
    setGroupActive([...openGroup]);
  };

  // ** Menu Group Click
  const handleGroupClick = () => {
    const openGroup = groupActive;
    if (verticalNavToggleType === 'collapse') {
      if (openGroup.includes(item.title)) {
        openGroup.splice(openGroup.indexOf(item.title), 1);
      } else {
        openGroup.push(item.title);
      }
      setGroupActive([...openGroup]);
    } else {
      toggleActiveGroup(item, parent);
    }
  };

  useEffect(() => {
    if (hasActiveChild(item, currentURL)) {
      if (!groupActive.includes(item.title)) groupActive.push(item.title);
    } else {
      const index = groupActive.indexOf(item.title);
      if (index > -1) groupActive.splice(index, 1);
    }
    setGroupActive([...groupActive]);
    setCurrentActiveGroup([...groupActive]);

    // Empty Active Group When Menu is collapsed and not hovered, to fix issue route change
    if (navCollapsed && !navHover) {
      setGroupActive([]);
    }
  }, [router.asPath]);

  useEffect(() => {
    if (navCollapsed && !navHover) {
      setGroupActive([]);
    }

    if ((navCollapsed && navHover) || (groupActive.length === 0 && !navCollapsed)) {
      setGroupActive([...currentActiveGroup]);
    }
  }, [navCollapsed, navHover]);

  useEffect(() => {
    if (groupActive.length === 0 && !navCollapsed) {
      setGroupActive([]);
    }
  }, [navHover]);

  const icon = parent && !item.icon ? themeConfig.config.navSubItemIcon : item.icon;
  const prefix = item.prefix;

  const menuGroupCollapseStyles = hideNavLabels ? { opacity: 0 } : { opacity: 1 };
  const subItemContentPl = isSubToSub ? 9 : parent ? 3 : 0;

  return (
    <CanViewNavLink navLink={item}>
      <ListItem
        disablePadding
        className="nav-group"
        onClick={handleGroupClick}
        sx={{ flexDirection: 'column' }}
      >
        <ListItemButton
          className={clsx({
            'Mui-selected':
              groupActive.includes(item.title) || currentActiveGroup.includes(item.title),
          })}
          sx={{
            py: 2.25,
            width: '100%',
            pl: `${collapsedIconInsetPx}px`,
            pr: 4,
            '&.Mui-selected': {
              backgroundColor: 'action.hover',
              '&:hover': {
                backgroundColor: 'action.hover',
              },
            },
            '&.Mui-selected.Mui-focusVisible': {
              backgroundColor: 'action.focus',
              '&:hover': {
                backgroundColor: 'action.focus',
              },
            },
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              width: '100%',
              minWidth: 0,
              ...(subItemContentPl ? { pl: subItemContentPl } : {}),
            }}
          >
            {isSubToSub ? null : (
              <ListItemIcon
                sx={{
                  color: 'text.primary',
                  minWidth: NAV_ITEM_ICON_WIDTH_PX,
                  mr: NAV_ITEM_ICON_LABEL_GAP,
                  fontSize: '1.3rem',
                }}
              >
                <FaIconUvilo icon={icon || ''} prefix={prefix as IconPrefix} />
              </ListItemIcon>
            )}
            <MenuItemTextWrapper
              sx={{
                ...hiddenNavLabelSx(hideNavLabels),
              }}
            >
              <Typography
                {...((themeConfig.config.menuTextTruncate ||
                  (!themeConfig.config.menuTextTruncate && navCollapsed && !navHover)) && {
                  noWrap: true,
                })}
              >
                {item.title}
              </Typography>
              <Box
                className="menu-item-meta"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  '& svg': {
                    color: 'text.primary',
                    transition: 'transform .25s ease-in-out',
                    ...(groupActive.includes(item.title) && {
                      transform: 'rotate(180deg)',
                    }),
                  },
                }}
              >
                {item.badgeContent ? (
                  <Chip
                    label={item.badgeContent}
                    color={item.badgeColor || 'primary'}
                    sx={{
                      mr: 1.5,
                      height: 20,
                      fontWeight: 500,
                      '& .MuiChip-label': {
                        px: 1.5,
                        textTransform: 'capitalize',
                      },
                    }}
                  />
                ) : null}
                <FaIconUvilo icon="chevron-down" />
              </Box>
            </MenuItemTextWrapper>
          </Box>
        </ListItemButton>
        <Collapse
          component="ul"
          onClick={(e) => e.stopPropagation()}
          in={groupActive.includes(item.title)}
          sx={{
            pl: 0,
            width: '100%',
            ...menuGroupCollapseStyles,
          }}
        >
          <VerticalNavItems
            {...props}
            parent={item}
            navVisible={true}
            verticalNavItems={item.children}
            isSubToSub={parent && item.children ? item : undefined}
          />
        </Collapse>
      </ListItem>
    </CanViewNavLink>
  );
};

export default VerticalNavGroup;
